import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CheckCircle2, ArrowRight, Send, AlertCircle } from 'lucide-react';
import PageSEO from '../components/PageSEO';

const contactSEO = {
  title: 'Contact Lidav Enterprises | Free Quote & Site Assessment – Nairobi, Kenya',
  description: 'Contact Lidav Enterprises Limited for a free site assessment and quote. Call 0722 148 999, email info@lidaventerprises.com or visit Utalii House, Uhuru Highway, Nairobi. Response within 1 hour.',
  canonical: '/contact',
  schema: {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ContactPage',
        name: 'Contact Lidav Enterprises Limited',
        url: 'https://lidaventerprises.com/contact',
        description: 'Get in touch with Lidav Enterprises for a free quote and site assessment. NEMA-licensed facilities management services across Kenya.',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://lidaventerprises.com' },
            { '@type': 'ListItem', position: 2, name: 'Contact Us', item: 'https://lidaventerprises.com/contact' },
          ],
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How quickly does Lidav Enterprises respond to urgent pest control or fumigation requests?',
            acceptedAnswer: { '@type': 'Answer', text: 'Lidav Enterprises offers same-day emergency response for urgent fumigation, pest control and hazmat situations. Call 0722 148 999 for immediate assistance. Our 24/7 emergency line is always available.' },
          },
          {
            '@type': 'Question',
            name: 'Does Lidav Enterprises serve clients outside Nairobi?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. Lidav Enterprises covers all of Greater Nairobi and serves clients across Kenya including Mombasa, Kisumu, Nakuru and Eldoret. We also handle national corporate and government contracts.' },
          },
          {
            '@type': 'Question',
            name: 'Are Lidav\'s fumigation chemicals safe for children and pets?',
            acceptedAnswer: { '@type': 'Answer', text: 'All Lidav chemicals are NEMA-approved and meet international safety standards. We provide full safety data sheets (SDS) and advise on recommended re-entry times after treatment. Environmentally responsible methods are used on every job.' },
          },
          {
            '@type': 'Question',
            name: 'How do I get a free site assessment from Lidav Enterprises?',
            acceptedAnswer: { '@type': 'Answer', text: 'Call 0722 148 999 or fill in the online enquiry form at lidaventerprises.com/contact. Our team will schedule a free visit to your facility, assess the scope of work and provide a detailed no-obligation quote.' },
          },
        ],
      },
    ],
  },
};

const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 36, scale: 0.98 },
  whileInView: { opacity: 1, y: 0,  scale: 1    },
  viewport:    { once: true, margin: '-80px'     },
  transition:  { duration: 0.6, ease: 'easeOut', delay },
});
const stagger     = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };
const staggerItem = {
  hidden: { opacity: 0, y: 28, scale: 0.97 },
  show:   { opacity: 1, y: 0,  scale: 1, transition: { duration: 0.5 } },
};

const serviceOptions = [
  'Fumigation & Pest Control',
  'Sanitary Disposal Services',
  'Hazards & Industrial Waste Management',
  'General Waste Management',
  'Landscaping Services',
  'Multiple Services',
];

const contactInfo = [
  { icon: Phone,  color: 'bg-brand-blue', label: 'Phone',    primary: '0722 148 999',             secondary: 'Call or WhatsApp us anytime',  href: 'tel:0722148999' },
  { icon: Mail,   color: 'bg-brand-red',  label: 'Email',    primary: 'info@lidaventerprises.com', secondary: 'We reply within 2 hours',     href: 'mailto:info@lidaventerprises.com' },
  { icon: MapPin, color: 'bg-brand-red',  label: 'Location', primary: 'Utalii House — Uhuru Hwy',  secondary: 'Nairobi, Kenya',              href: 'https://maps.google.com/?q=Utalii+House+Uhuru+Highway+Nairobi' },
  { icon: Clock,  color: 'bg-purple-600', label: 'Hours',    primary: 'Mon–Sat: 7 AM – 7 PM',      secondary: '24/7 emergency line available', href: null },
];

export default function Contact() {
  const [form, setForm]           = useState({ name: '', phone: '', email: '', service: '', message: '' });
  const [errors, setErrors]       = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [sendError, setSendError] = useState('');

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = 'Full name is required.';
    if (!form.phone.trim())   e.phone   = 'Phone number is required.';
    if (!form.email.trim())   e.email   = 'Email address is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Please enter a valid email.';
    if (!form.service)        e.service = 'Please select a service.';
    if (!form.message.trim()) e.message = 'Message is required.';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    setSendError('');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          subject:    `New Enquiry — ${form.service} from ${form.name}`,
          from_name:  'Lidav Website',
          replyto:    form.email,
          name:       form.name,
          email:      form.email,
          phone:      form.phone,
          service:    form.service,
          message:    form.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        throw new Error(data.message);
      }
    } catch {
      setSendError('Failed to send message. Please call us directly on 0722 148 999.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <PageSEO {...contactSEO} />
      {/* ── Hero ── */}
      <section className="relative min-h-[45vh] flex items-center bg-brand-blue overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=70&fm=webp"
            alt="Contact"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/95 to-brand-blue/80" />
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 0)', backgroundSize: '28px 28px' }} />
        <div className="pointer-events-none absolute -top-20 right-0 w-80 h-80 bg-brand-red/6 rounded-full blur-3xl animate-blob" style={{ animationDelay: '1s' }} />
        <div className="pointer-events-none absolute bottom-0 left-1/3 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-blob" style={{ animationDelay: '3.5s' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div
              className="inline-flex items-center gap-2 text-gray-700 text-xs font-inter mb-5 px-3 py-1.5 rounded-full border"
              style={{
                background:           'rgba(0,0,0,0.07)',
                backdropFilter:       'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                borderColor:          'rgba(0,0,0,0.08)',
              }}
            >
              <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-gray-900 font-semibold">Contact Us</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-poppins text-gray-900 mb-4">
              Get In <span className="text-brand-red">Touch</span>
            </h1>
            <p className="text-gray-700 text-lg font-inter max-w-xl font-medium">
              Request a free quote, ask a question or book a site assessment. Our team responds within 1 hour.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-14 bg-white" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </section>

      {/* ── Contact Info Cards ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {contactInfo.map(({ icon: Icon, color, label, primary, secondary, href }) => (
              <motion.div key={label} variants={staggerItem}>
                {href ? (
                  <a
                    href={href}
                    target={href.startsWith('http') ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className="block rounded-2xl p-5 transition-all duration-300 group cursor-pointer hover:-translate-y-1"
                    style={{
                      background:           'rgba(255,255,255,0.88)',
                      backdropFilter:       'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border:               '1px solid rgba(226,232,240,0.8)',
                      boxShadow:            '0 4px 20px rgba(2,174,242,0.06)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 12px 40px rgba(2,174,242,0.14)'; e.currentTarget.style.borderColor = 'rgba(2,174,242,0.20)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(2,174,242,0.06)'; e.currentTarget.style.borderColor = 'rgba(226,232,240,0.8)'; }}
                  >
                    <ContactCard icon={Icon} color={color} label={label} primary={primary} secondary={secondary} />
                  </a>
                ) : (
                  <div
                    className="rounded-2xl p-5 cursor-default"
                    style={{
                      background:   'rgba(248,250,252,1)',
                      border:       '1px solid rgba(226,232,240,0.8)',
                      boxShadow:    '0 4px 20px rgba(0,0,0,0.03)',
                    }}
                  >
                    <ContactCard icon={Icon} color={color} label={label} primary={primary} secondary={secondary} />
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Form + Map ── */}
      <section className="py-16 bg-[#f0f7ff] relative overflow-hidden">
        <div className="pointer-events-none absolute top-0 left-0 w-64 h-64 bg-brand-blue/4 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 right-0 w-64 h-64 bg-brand-red/3 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-start">

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div
                className="rounded-3xl p-8 sm:p-10"
                style={{
                  background:           'rgba(255,255,255,0.90)',
                  backdropFilter:       'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border:               '1px solid rgba(255,255,255,0.70)',
                  boxShadow:            '0 16px 56px rgba(2,174,242,0.10)',
                }}
              >
                {!submitted ? (
                  <>
                    <div className="mb-8">
                      <span className="section-tag">Send Us a Message</span>
                      <h2 className="text-2xl sm:text-3xl font-bold font-poppins text-gray-900 mt-1">
                        Request a Free Quote
                      </h2>
                      <p className="text-gray-500 font-inter text-sm mt-2">
                        Fill in your details and we will respond within 1 business hour.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} noValidate className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="form-label">Full Name <span className="text-brand-red">*</span></label>
                          <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="John Mwangi"
                            className={`form-input ${errors.name ? 'border-brand-red ring-brand-red/20 ring-2' : ''}`} />
                          {errors.name && <p className="text-brand-red text-xs mt-1 font-inter">{errors.name}</p>}
                        </div>
                        <div>
                          <label className="form-label">Phone Number <span className="text-brand-red">*</span></label>
                          <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="0722 148 999"
                            className={`form-input ${errors.phone ? 'border-brand-red ring-brand-red/20 ring-2' : ''}`} />
                          {errors.phone && <p className="text-brand-red text-xs mt-1 font-inter">{errors.phone}</p>}
                        </div>
                      </div>

                      <div>
                        <label className="form-label">Email Address <span className="text-brand-red">*</span></label>
                        <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="you@company.com"
                          className={`form-input ${errors.email ? 'border-brand-red ring-brand-red/20 ring-2' : ''}`} />
                        {errors.email && <p className="text-brand-red text-xs mt-1 font-inter">{errors.email}</p>}
                      </div>

                      <div>
                        <label className="form-label">Service Interested In <span className="text-brand-red">*</span></label>
                        <select name="service" value={form.service} onChange={handleChange}
                          className={`form-input ${errors.service ? 'border-brand-red ring-brand-red/20 ring-2' : ''}`}>
                          <option value="">Select a service...</option>
                          {serviceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                        {errors.service && <p className="text-brand-red text-xs mt-1 font-inter">{errors.service}</p>}
                      </div>

                      <div>
                        <label className="form-label">Message <span className="text-brand-red">*</span></label>
                        <textarea name="message" value={form.message} onChange={handleChange} rows={5}
                          placeholder="Describe your requirements — location, facility type, urgency, etc."
                          className={`form-input resize-none ${errors.message ? 'border-brand-red ring-brand-red/20 ring-2' : ''}`} />
                        {errors.message && <p className="text-brand-red text-xs mt-1 font-inter">{errors.message}</p>}
                      </div>

                      {sendError && (
                        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-inter">
                          <AlertCircle size={15} className="flex-shrink-0" />
                          {sendError}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-2 bg-brand-blue hover:bg-brand-blue-dark disabled:opacity-70 text-gray-900 py-3.5 rounded-xl font-semibold font-poppins text-sm transition-all duration-200 hover:-translate-y-0.5 shadow-md hover:shadow-lg cursor-pointer"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                            Sending...
                          </span>
                        ) : (
                          <><Send size={15} /> Send Message &amp; Request Quote</>
                        )}
                      </button>

                      <p className="text-gray-400 text-xs font-inter text-center">
                        By submitting, you agree to be contacted by Lidav Enterprises regarding your enquiry.
                      </p>
                    </form>
                  </>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                      style={{ background: 'linear-gradient(135deg, rgba(2,174,242,0.12), rgba(2,174,242,0.06))' }}
                    >
                      <CheckCircle2 size={40} className="text-brand-blue" />
                    </div>
                    <h3 className="text-2xl font-bold font-poppins text-gray-900 mb-3">Message Received</h3>
                    <p className="text-gray-500 font-inter text-sm leading-relaxed max-w-sm mx-auto mb-7">
                      Thank you for contacting Lidav Enterprises. We will review your enquiry and respond within 1 business hour.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setForm({ name: '', phone: '', email: '', service: '', message: '' }); }}
                      className="inline-flex items-center gap-2 border-2 border-brand-blue text-brand-blue px-6 py-2.5 rounded-full text-sm font-bold hover:bg-brand-blue hover:text-gray-900 transition-all cursor-pointer"
                    >
                      Send Another Enquiry
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Map + Info */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="space-y-6"
            >
              {/* Map embed */}
              <div
                className="rounded-3xl overflow-hidden"
                style={{
                  background:           'rgba(255,255,255,0.90)',
                  backdropFilter:       'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border:               '1px solid rgba(255,255,255,0.70)',
                  boxShadow:            '0 8px 40px rgba(2,174,242,0.08)',
                }}
              >
                <div className="relative overflow-hidden" style={{ height: '300px' }}>
                  <iframe
                    title="Lidav Enterprises — Utalii House, Uhuru Highway"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15955.268894214369!2d36.81635817178162!3d-1.283536389116547!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f10d22f42bf35%3A0x449d7ec7b378dfeb!2sUtalii%20House!5e0!3m2!1sen!2ske!4v1778055675843!5m2!1sen!2ske"
                    width="100%"
                    height="300"
                    style={{ border: 0, display: 'block' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-full"
                  />
                </div>
                <div className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand-red/10 rounded-xl flex items-center justify-center">
                      <MapPin size={18} className="text-brand-red" />
                    </div>
                    <div>
                      <p className="font-semibold font-poppins text-gray-900 text-sm">Utalii House — Uhuru Highway</p>
                      <p className="text-gray-400 text-xs font-inter">Nairobi, Kenya</p>
                    </div>
                  </div>
                  <a
                    href="https://maps.google.com/?q=Utalii+House+Uhuru+Highway+Nairobi"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-blue text-xs font-semibold font-inter hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    Get Directions <ArrowRight size={11} />
                  </a>
                </div>
              </div>

              {/* Quick contact — glass on brand-blue */}
              <div
                className="rounded-3xl p-7"
                style={{
                  background:           'rgba(2,174,242,0.92)',
                  backdropFilter:       'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border:               '1px solid rgba(255,255,255,0.2)',
                  boxShadow:            '0 8px 32px rgba(2,174,242,0.25)',
                }}
              >
                <h3 className="font-bold font-poppins text-gray-900 text-lg mb-2">Prefer to speak directly?</h3>
                <p className="text-gray-800 font-inter text-sm mb-5 leading-relaxed font-medium">
                  Our customer service team is available Monday to Saturday, 7 AM to 7 PM. For emergencies, our 24/7 hotline is always on.
                </p>
                <a
                  href="tel:0722148999"
                  className="flex items-center gap-3 text-gray-900 font-bold font-poppins py-3 px-5 rounded-xl text-sm transition-all w-fit cursor-pointer hover:-translate-y-0.5"
                  style={{ background: 'rgba(0,0,0,0.10)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
                >
                  <Phone size={17} />
                  Call 0722 148 999
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()} className="text-center mb-10">
            <span className="section-tag">Common Questions</span>
            <h2 className="text-2xl sm:text-3xl font-bold font-poppins text-gray-900 mt-2">Frequently Asked Questions</h2>
          </motion.div>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="space-y-3"
          >
            {[
              { q: 'How quickly can you respond to an urgent request?',   a: 'We offer same-day response for emergency fumigation, pest control and cleaning requests. Call 0722 148 999 for urgent assistance.' },
              { q: 'Do you serve clients outside Nairobi?',               a: 'Yes. We cover the entire Greater Nairobi area and serve clients across Kenya including Mombasa, Kisumu, Nakuru and Eldoret.' },
              { q: 'Are your chemicals safe for children and pets?',       a: 'All our chemicals are NEMA-approved and meet international safety standards. We provide full safety data sheets and advise on re-entry times after treatment.' },
              { q: 'What is included in a free site assessment?',          a: 'Our team visits your facility, assesses the scope of work, identifies specific issues and provides a detailed, no-obligation quote tailored to your needs.' },
            ].map(({ q, a }) => (
              <motion.details
                key={q}
                variants={staggerItem}
                className="rounded-2xl overflow-hidden group"
                style={{
                  background:           'rgba(255,255,255,0.88)',
                  backdropFilter:       'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  border:               '1px solid rgba(226,232,240,0.8)',
                  boxShadow:            '0 2px 12px rgba(2,174,242,0.04)',
                }}
              >
                <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold font-poppins text-gray-900 text-sm list-none select-none">
                  {q}
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ml-4 group-open:bg-brand-blue transition-colors"
                    style={{ background: 'rgba(2,174,242,0.10)' }}
                  >
                    <span className="text-brand-blue group-open:text-white text-xs font-bold transition-colors">+</span>
                  </span>
                </summary>
                <p className="px-5 pb-5 text-gray-500 text-sm font-inter leading-relaxed">{a}</p>
              </motion.details>
            ))}
          </motion.div>
        </div>
      </section>
    </main>
  );
}

function ContactCard({ icon: Icon, color, label, primary, secondary }) {
  return (
    <div className="flex items-start gap-4">
      <div className={`w-11 h-11 ${color} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-sm`}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-gray-400 text-xs font-inter mb-0.5">{label}</p>
        <p className="font-bold font-poppins text-gray-900 text-sm">{primary}</p>
        <p className="text-gray-400 text-xs font-inter mt-0.5">{secondary}</p>
      </div>
    </div>
  );
}
